import type { MouseEvent } from 'react';
import { useContext, useRef, useState } from 'react';
import { increaseViewCount, postItemText } from 'common/utils';
import { checkIsContentLocked } from 'common/utils/new-post';
import RichContent from 'components/common/RichContent';
import { Button } from 'components/v2/atoms/button';
import { useRouter } from 'next/router';
import { PostTypeEnum } from 'service/timeline';
import { getUserProfile } from 'service/user';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

import { PostItemContext } from './post-item-context';

const TextContent = ({ isHtmlContent }: { isHtmlContent?: boolean }) => {
  const { post } = useContext(PostItemContext);
  const router = useRouter();
  const currentUser = useStore((state) => state.currentUser);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const showPaymentDialog = useGlobalStore((state) => state.showPaymentDialog);
  const { text } = post;
  const [expanded, setExpanded] = useState(false);
  const [showSubscriptionCta, setShowSubscriptionCta] = useState(false);
  const [isLoadingCreator, setIsLoadingCreator] = useState(false);
  const hasSentReadCount = useRef(false);

  if (!text || text.trim() === '') {
    return null;
  }
  const trimmedText = text?.trim();
  const displayText = postItemText(trimmedText, 2);
  const isTruncated = displayText !== trimmedText;
  const isContentLocked = checkIsContentLocked(post);
  const isCreatorPaidPost = [PostTypeEnum.Paid, PostTypeEnum.Plus].includes(
    post.type,
  );
  const isShowingLockedPreview =
    showSubscriptionCta && isContentLocked && isCreatorPaidPost;
  const shownText =
    expanded || isShowingLockedPreview ? trimmedText : displayText;
  const postDetailsUrl = `/${post.user_info?.username}/posts/${post.id}`;
  const subscriptionButtonText = currentUser
    ? post.type === PostTypeEnum.Plus
      ? 'プライムPlusを無料体験'
      : 'プライム登録を無料体験'
    : '無料でログインする';
  const handleSubscriptionClick = async () => {
    if (!currentUser) {
      showLogin({ authorName: post.user_info?.name });
      return;
    }
    if (!post.user_info?.username || isLoadingCreator) return;
    setIsLoadingCreator(true);
    try {
      const { data: creator } = await getUserProfile(post.user_info.username);
      showPaymentDialog({ creator });
    } finally {
      setIsLoadingCreator(false);
    }
  };
  const handleReadMoreClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!hasSentReadCount.current) {
      increaseViewCount(post.id);
      hasSentReadCount.current = true;
    }
    if (isContentLocked) {
      if (isCreatorPaidPost) {
        setShowSubscriptionCta(true);
        return;
      }
      void router.push(postDetailsUrl);
      return;
    }
    setExpanded(true);
  };

  return (
    <div className={`text-base text-primary`}>
      <div
        className={`relative text-base ${
          expanded
            ? 'whitespace-pre-line'
            : isShowingLockedPreview
              ? 'line-clamp-3 overflow-hidden whitespace-pre-line'
              : 'line-clamp-2'
        }`}
      >
        {isHtmlContent ? (
          <div dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          <RichContent text={shownText} />
        )}
        {isShowingLockedPreview && (
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent from-35% to-white" />
        )}
      </div>

      {isTruncated && !expanded && !showSubscriptionCta && (
        <button
          type="button"
          onClick={handleReadMoreClick}
          className="mt-1 text-sm font-normal text-pp-green-primary transition-colors hover:text-pp-green-hover hover:underline"
        >
          ...続きを読む
        </button>
      )}
      {showSubscriptionCta && (
        <div className="mt-5 flex justify-center">
          <Button
            type="button"
            variant="primaryGreen"
            size="lg"
            className="rounded-md px-8 font-bold"
            disabled={isLoadingCreator}
            onClick={(e) => {
              e.stopPropagation();
              void handleSubscriptionClick();
            }}
          >
            {isLoadingCreator ? '読み込み中...' : subscriptionButtonText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TextContent;
